import { Component, OnInit, OnDestroy }                from '@angular/core';
import { ActivatedRoute, Params, Router, NavigationEnd }   from '@angular/router';
import { Location }                         from '@angular/common';
import { FormGroup }                        from '@angular/forms';
import { DomSanitizer, SafeResourceUrl }    from '@angular/platform-browser';

import { ProgramSingle }                    from './../../../models/program/program-single';
import { RouteOptions }                     from './../../../models/route-options';
import { Wave }                             from './../../../models/program/wave';
import { ProgramService }                   from './../../../shared/services/program.service';
import { ProgramRoutesService }             from './program-routes.service';
import { GroupService }                     from './../group.service';
import { InTeamLevelService }               from './in-team-level.service';
import { BatchResponse }                    from '../../../models/program/batch-response';
import { BatchCommitInfo }                  from '../../../models/program/batch-commit-info';
import { Subscription } from 'rxjs/Subscription';
import { GroupRefreshService } from './group-refresh.service';
import { EditionListService } from '../../../editions/edition.service';
import { NewUser } from '../../../models/program/new-user';
import { NewAndExistingUsers } from '../../../models/program/new-and-existing-users';
import { ExistingUser } from '../../../models/program/existing-user';
import { PermissionsService } from '../../../shared/services/permissions.service';

@Component({
  selector: 'app-program',
  templateUrl: './program.component.html',
  styleUrls: [ './program.component.less' ],
  providers: [ InTeamLevelService, GroupRefreshService ]
})

export class ProgramComponent implements OnInit, OnDestroy {
  public program: ProgramSingle;
  public programId: number;
  public waves: Wave[];
  public newAndExistingUserList: NewAndExistingUsers;
  public routeName: any;
  public fromExisting = false;
  public showUsersAdd = false;
  public moveTeam = false;
  public deleteTeam = false;
  public groupId: number;
  public batchResponse: BatchResponse;
  public groupSetup;
  public inTeam = true;
  public groupSettings: { inputType: string, programId: number, batchCommitInfo: BatchCommitInfo };
  public links: RouteOptions[] = [
    { ref: ['teams'],            title: 'Waves & Teams'                                       },
    { ref: ['appearance'],       title: 'Appearance',     permission: 'nomadicAdministration' },
    { ref: ['admins'],           title: 'Admins'                                              },
    { ref: ['content'],          title: 'Content',        permission: 'nomadicAdministration' },
    { ref: ['other-settings'],   title: 'Other Settings'                                      },
    { ref: ['changes'],          title: 'Change Log',     permission: 'nomadicAdministration' }
  ];
  public linksFilteredForPermissions: RouteOptions[]
  public previewUrl: SafeResourceUrl = null;
  public minimalStateObj: {categoryType: string; categoryId: number};
  private subs: Subscription[] = [];

  constructor(
    private programService: ProgramService,
    private groupService: GroupService,
    private groupRefreshService: GroupRefreshService,
    private editionService: EditionListService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private _programRoutes: ProgramRoutesService,
    private inTeamLevelService: InTeamLevelService,
    private permissionsService: PermissionsService,
    private sanitizer: DomSanitizer
  ) {
    this.setHeader();
    this.subs.push(this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.minimalStateObj = this.retrieveMinimalStateObjFromUrl(event.url);
        this.inTeamCheck(this.route);
      }
    }));
  }

  ngOnInit(): void {
    this.permissionsService.retrievePermissions().subscribe(permissions => {
      this.linksFilteredForPermissions = this.links.filter(link => link.permission ? permissions[link.permission] : true);
    });

    this.subs.push(this.route.params.subscribe((params: Params) => {
      this.inTeamCheck(this.route);
      let id = +params['id'];
      this.programId = id;
      this.links.push({ ref: ['/analytics/snapshot', {program: this.programId}], title: 'Go to Analytics' });
      this.subs.push(this.programService
          .getProgram(id)
          .subscribe(program => this.program = program)
      );
      this.subs.push(this.groupService
          .getWaves(id)
          .subscribe(waves => this.waves = waves)
      );
    }));
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  /**
   * Public Methods
   */

  public batchWatch(batchResponse: BatchResponse) {
    this.batchResponse = batchResponse;
  }

  public newAndExistingUserListWatch(obj: NewAndExistingUsers): void {
    this.fromExisting = true;
    this.showUsersAdd = false;
    this.newAndExistingUserList = obj.map(user => {
      if (user.id > 0) {
        let existingUser = <ExistingUser>user;
        return {name: existingUser.name, id: existingUser.id};
      } else {
        return user;
      }
    });
  }
  public resetData(): void {
    this.groupRefreshService.refreshGroupList.next(true);
    this.editionService.refreshCurrentEdition();
    this.showUsersAdd = false;
    this.batchResponse = null;
    this.groupSettings = null;
    this.fromExisting = false;
    this.newAndExistingUserList = null;
  }

  public addMenuItemSelected(itemName: string): void {
    if (itemName === 'addUsersTeams') {
      this.showUsersAdd = !this.showUsersAdd;
    } else if (itemName === 'moveTeam') {
      this.moveTeam = !this.moveTeam;
    } else if (itemName === 'deleteTeam') {
      this.deleteTeam = !this.deleteTeam;
    } else if (itemName === 'openPlatform') {
      window.open(`/library?groupselect=${this.groupId}`);
    }
  }

  public teamDeleted(): void {
    this.deleteTeam = false;
    this.router.navigate(['dashboard', 'programs', this.programId, 'teams']);
  }

  public loadSettingsFromBatch(e: { inputType: string, programId: number, batchCommitInfo: BatchCommitInfo }): void {
    this.groupSettings = { inputType: e.inputType, programId: e.programId, batchCommitInfo: e.batchCommitInfo };
    this.showUsersAdd = false;
  }

  public closeAdd(reset: boolean): void {
    this.showUsersAdd = false;
    window.location.reload();
  }

  /**
   * Private Methods
   */

  private setHeader(): void {
    this._programRoutes.name.subscribe(n => {
      this.routeName = n;
      this.groupId = this.groupService._groupId;
    });
  }

  private inTeamCheck(route): void {
    let level = 0;
    let recursiveRoute = route.children[0];
    while (recursiveRoute.children.length > 0) {
      recursiveRoute = recursiveRoute.children[0];
      level++;
    }
    this.inTeamLevelService.inTeamLevel.next(level);
    this.inTeam = (level > 0);
  }

  private setPreviewUrl(programId: number) {
    this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`/programs/${programId}/landing/preview`);
  }

  // "/dashboard/programs/237/waves/137/teams/1506/users"

  private retrieveMinimalStateObjFromUrl(url: string): {categoryType: string; categoryId: number} {
    let categoryType: string, categoryId: number;
    if (url.split('teams/')[1]) {
      categoryType = 'group';
      let firstSplit = url.split('teams')[1].split('/')[1];
      categoryId = +firstSplit.split('?')[0];
    } else if (url.split('waves/')[1]) {
      categoryType = 'wave';
      let firstSplit = url.split('waves')[1].split('/')[1];
      categoryId = +firstSplit.split('?')[0];
    } else if (url.split('programs/')[1]) {
      categoryType = 'program';
      let firstSplit = url.split('programs')[1].split('/')[1];
      categoryId = +firstSplit.split('?')[0];
    }
    return {
      categoryType,
      categoryId
    }
  }
}



// WEBPACK FOOTER //
// /home/runner/work/nomadic-admin/nomadic-admin/nomadic-admin/src/app/dashboard/program-list/program/program.component.ts